/**
 * ArUco marker detection service.
 * Wraps the global AR.Detector from public/aruco.js (js-aruco library).
 * Uses the 4x4_50 ArUco dictionary.
 */

import type { DetectedMarker, Point2D } from './types';

/** Shape of the global AR namespace from aruco.js */
interface ArucoMarkerResult {
  id: number;
  corners: Array<{ x: number; y: number }>;
}

interface ArucoDetectorGlobal {
  detect(image: { width: number; height: number; data: Uint8ClampedArray }): ArucoMarkerResult[];
}

interface ARNamespace {
  Detector: new () => ArucoDetectorGlobal;
}

/** Access the global AR namespace (loaded via script tag) */
function getARNamespace(): ARNamespace | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const g = globalThis as any;
  if (g.AR && typeof g.AR.Detector === 'function') {
    return g.AR as ARNamespace;
  }
  return null;
}

/**
 * ArUco marker detector. Reuses a single AR.Detector instance
 * to avoid garbage collection pressure from repeated allocations.
 */
export class ArucoDetector {
  private detector: ArucoDetectorGlobal | null = null;

  constructor() {
    this.initDetector();
  }

  private initDetector(): void {
    const ar = getARNamespace();
    if (ar) {
      this.detector = new ar.Detector();
    }
  }

  /**
   * Detect ArUco markers in an ImageData frame.
   * Returns empty array if aruco.js is not loaded.
   */
  detect(imageData: ImageData): DetectedMarker[] {
    // Lazy init: try again if aruco.js wasn't loaded at construction time
    if (!this.detector) {
      this.initDetector();
      if (!this.detector) return [];
    }

    const results = this.detector.detect({
      width: imageData.width,
      height: imageData.height,
      data: imageData.data,
    });

    return results.map((marker) => ({
      markerType: 'aruco' as const,
      markerId: String(marker.id),
      corners: marker.corners.slice(0, 4).map((c) => ({ x: c.x, y: c.y })) as [
        Point2D,
        Point2D,
        Point2D,
        Point2D,
      ],
    }));
  }

  /** Check if the aruco.js library is available */
  isAvailable(): boolean {
    if (this.detector) return true;
    this.initDetector();
    return this.detector !== null;
  }
}
